/**
 * useBadges Hook
 * Manages badge state and provides functions to record game completions
 */

import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useBadges(playerId) {
  const [newBadges, setNewBadges] = useState([]);
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [progress, setProgress] = useState(null);

  // Get current badge to display
  const currentBadge = newBadges[currentBadgeIndex] || null;

  // Record game completion and check for new badges
  const recordGameCompletion = useCallback(async (gameType, score, isPerfect = false, subject = null, ageGroup = null) => {
    if (!playerId) {
      console.warn('No player ID for badge tracking');
      return { newBadges: [] };
    }

    try {
      const response = await axios.post(`${API}/badges/record-completion`, {
        player_id: playerId,
        game_type: gameType,
        score: score,
        perfect: isPerfect,
        subject: subject,
        age_group: ageGroup
      });

      const data = response.data;

      if (data.new_badges && data.new_badges.length > 0) {
        setNewBadges(data.new_badges);
        setCurrentBadgeIndex(0);
        setShowNotification(true);
      }

      if (data.progress) {
        setProgress(data.progress);
      }

      return {
        newBadges: data.new_badges || [],
        totalBadges: data.total_badges,
        progress: data.progress
      };
    } catch (error) {
      console.error('Error recording game completion:', error);
      return { newBadges: [] };
    }
  }, [playerId]);

  // Handle closing badge notification
  const handleBadgeClose = useCallback(() => {
    if (currentBadgeIndex < newBadges.length - 1) {
      // Show next badge
      setCurrentBadgeIndex(prev => prev + 1);
    } else {
      // All badges shown
      setShowNotification(false);
      setNewBadges([]);
      setCurrentBadgeIndex(0);

      // Mark badges as notified
      if (playerId) {
        axios.post(`${API}/badges/mark-notified/${playerId}`).catch(console.error);
      }
    }
  }, [currentBadgeIndex, newBadges.length, playerId]);

  // Fetch player's badge progress
  const fetchProgress = useCallback(async () => {
    if (!playerId) return null;

    try {
      const response = await axios.get(`${API}/badges/player/${playerId}`);
      setProgress(response.data.progress);
      return response.data;
    } catch (error) {
      console.error('Error fetching badge progress:', error);
      return null;
    }
  }, [playerId]);

  return {
    recordGameCompletion,
    currentBadge,
    showNotification,
    handleBadgeClose,
    progress,
    fetchProgress,
    totalNewBadges: newBadges.length,
    currentBadgeNumber: currentBadgeIndex + 1
  };
}

export default useBadges;
